import React from 'react';
import { Plus, Edit, Trash2, Search, Users, Calendar, Filter, FileText, DollarSign, Check, X } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { caravanaService, igrejaService, eventoService, parcelaCaravanaService } from '../services/api';

const Caravanas = () => {
  const { data: caravanas, loading, error, create, update, remove, refresh } = useCrud(caravanaService, 'caravanas');
  const { data: igrejas } = useCrud(igrejaService, 'igrejas');
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const modal = useModal();
  const parcelasModal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedEvento, setSelectedEvento] = React.useState('');
  const [parcelas, setParcelas] = React.useState([]);
  const [caravanatSelecionada, setCaravanaSelecionada] = React.useState(null);
  const [parcelasLoading, setParcelasLoading] = React.useState(false);

  const validationRules = {
    nome: { required: true, label: 'Nome', minLength: 2, maxLength: 100 },
    igreja_id: { required: true, label: 'Igreja' },
    evento_id: { required: true, label: 'Evento' },
    qtd_homens: { 
      required: true, 
      label: 'Quantidade de homens',
      custom: (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num < 0) {
          return 'Quantidade deve ser um número maior ou igual a 0';
        }
        return null;
      }
    },
    qtd_mulheres: { 
      required: true, 
      label: 'Quantidade de mulheres',
      custom: (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num < 0) {
          return 'Quantidade deve ser um número maior ou igual a 0';
        }
        return null;
      }
    },
    valor: {
      custom: (value) => {
        if (value === '' || value === undefined) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 0) {
          return 'Valor deve ser um número maior ou igual a 0';
        }
        return null;
      }
    }
  };

  const form = useForm(
    { nome: '', igreja_id: '', evento_id: '', qtd_homens: '', qtd_mulheres: '', observacao: '', valor: '' },
    validationRules
  );

  const parcelaForm = useForm(
    { numero_parcela: '', valor: '', data_vencimento: '', data_pagamento: '', status: 'pendente', observacao: '' },
    {
      numero_parcela: { required: true, label: 'Número da Parcela' },
      valor: { required: true, label: 'Valor', custom: (v) => !v || parseFloat(v) <= 0 ? 'Valor deve ser maior que zero' : null },
      status: { required: true, label: 'Status' }
    }
  );

  const filteredCaravanas = caravanas.filter(caravana => {
    const matchesSearch = caravana.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caravana.Igreja?.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEvento = selectedEvento === '' || caravana.evento_id?.toString() === selectedEvento;
    
    return matchesSearch && matchesEvento;
  });

  const calcularValuePago = (parcelasList) => {
    return parcelasList
      .filter(p => p.status === 'pago')
      .reduce((acc, p) => acc + parseFloat(p.valor || 0), 0);
  };

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Nova Caravana' });
  };

  const handleEdit = (caravana) => {
    form.reset({
      nome: caravana.nome || '',
      igreja_id: caravana.igreja_id || '',
      evento_id: caravana.evento_id || '',
      qtd_homens: caravana.qtd_homens || '',
      qtd_mulheres: caravana.qtd_mulheres || '',
      observacao: caravana.observacao || '',
      valor: caravana.valor || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Caravana', id: caravana.id });
  };

  const handleDelete = async (caravana) => {
    if (window.confirm(`Deseja realmente excluir a caravana "${caravana.nome}"?`)) {
      const result = await remove(caravana.id);
      if (result.success) {
        refresh();
      }
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
      refresh();
    }
  };

  const handleListarParcelas = async (caravana) => {
    setCaravanaSelecionada(caravana);
    setParcelasLoading(true);
    try {
      const response = await parcelaCaravanaService.getByCaravana(caravana.id);
      setParcelas(response.data?.data || []);
    } catch (error) {
      console.error('Erro ao carregar parcelas:', error);
      setParcelas([]);
    }
    setParcelasLoading(false);
    parcelasModal.open({ title: `Parcelas de ${caravana.nome}` });
  };

  const handleAdicionarParcela = () => {
    parcelaForm.reset();
    parcelasModal.open({ 
      mode: 'addParcela',
      title: `Adicionar Parcela - ${caravanatSelecionada?.nome}`,
      subMode: 'create'
    });
  };

  const handlePagarParcela = (parcela) => {
    parcelaForm.reset({
      numero_parcela: parcela.numero_parcela || '',
      valor: parcela.valor || '',
      data_vencimento: parcela.data_vencimento || '',
      data_pagamento: new Date().toISOString().split('T')[0],
      status: 'pago',
      observacao: parcela.observacao || ''
    });
    parcelasModal.open({
      mode: 'editParcela',
      title: `Registrar Pagamento - Parcela ${parcela.numero_parcela}`,
      id: parcela.id
    });
  };

  const handleSubmitParcela = async (values) => {
    try {
      if (parcelasModal.data.mode === 'addParcela') {
        values.caravana_id = caravanatSelecionada.id;
        const response = await parcelaCaravanaService.create(values);
        if (response.data?.success || response.status === 200) {
          // Recarregar parcelas
          const updatedParcelas = await parcelaCaravanaService.getByCaravana(caravanatSelecionada.id);
          setParcelas(updatedParcelas.data?.data || []);
          // Atualizar lista de caravanas para refletir novo valor
          await refresh();
          parcelaForm.reset();
          // Voltar para a lista de parcelas
          parcelasModal.open({ title: `Parcelas de ${caravanatSelecionada?.nome}` });
        }
      } else if (parcelasModal.data.mode === 'editParcela') {
        const response = await parcelaCaravanaService.update(parcelasModal.data.id, values);
        if (response.data?.success || response.status === 200) {
          // Recarregar parcelas
          const updatedParcelas = await parcelaCaravanaService.getByCaravana(caravanatSelecionada.id);
          setParcelas(updatedParcelas.data?.data || []);
          // Atualizar lista de caravanas para refletir novo valor
          await refresh();
          parcelaForm.reset();
          // Voltar para a lista de parcelas
          parcelasModal.open({ title: `Parcelas de ${caravanatSelecionada?.nome}` });
        }
      }
    } catch (error) {
      console.error('Erro ao salvar parcela:', error);
      alert('Erro ao salvar parcela: ' + error.response?.data?.message || error.message);
    }
  };

  const handleDeletarParcela = async (parcela) => {
    if (window.confirm(`Deseja realmente excluir a parcela ${parcela.numero_parcela}?`)) {
      try {
        await parcelaCaravanaService.delete(parcela.id);
        const updatedParcelas = await parcelaCaravanaService.getByCaravana(caravanatSelecionada.id);
        setParcelas(updatedParcelas.data?.data || []);
        // Atualizar lista de caravanas para refletir novo valor
        await refresh();
      } catch (error) {
        console.error('Erro ao deletar parcela:', error);
        alert('Erro ao deletar parcela: ' + error.response?.data?.message || error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Caravanas</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Nova Caravana
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar caravanas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={selectedEvento}
              onChange={(e) => setSelectedEvento(e.target.value)}
              className="form-select pl-10 min-w-48"
            >
              <option value="">Todos os eventos</option>
              {eventos.map((evento) => (
                <option key={evento.id} value={evento.id}>
                  {evento.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            {filteredCaravanas.length} caravana(s) encontrada(s)
          </div>
        </div>
        {(searchTerm || selectedEvento) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Filtros ativos:</span>
            {searchTerm && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                Busca: "{searchTerm}"
              </span>
            )}
            {selectedEvento && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                Evento: {eventos.find(e => e.id.toString() === selectedEvento)?.nome}
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedEvento('');
              }}
              className="text-gray-400 hover:text-gray-600 ml-2"
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      <div className="card">
        {filteredCaravanas.length === 0 ? (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma caravana encontrada</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova caravana.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Caravana
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Igreja</th>
                  <th>Evento</th>
                  <th>Homens</th>
                  <th>Mulheres</th>
                  <th>Total</th>
                  <th>Valor</th>
                  <th>Valor Pago</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCaravanas.map((caravana) => {
                  const valorPago = calcularValuePago(caravana.Parcelas || []);
                  return (
                    <tr key={caravana.id}>
                      <td className="font-medium" data-label="Nome">{caravana.nome}</td>
                      <td data-label="Igreja">{caravana.Igreja?.nome || 'N/A'}</td>
                      <td data-label="Evento">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-sm">
                            {caravana.Evento?.nome || eventos.find(e => e.id === caravana.evento_id)?.nome || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td data-label="Homens">{caravana.qtd_homens || 0}</td>
                      <td data-label="Mulheres">{caravana.qtd_mulheres || 0}</td>
                      <td className="font-medium" data-label="Total">{(caravana.qtd_homens || 0) + (caravana.qtd_mulheres || 0)}</td>
                      <td data-label="Valor">
                        <div className="flex items-center gap-1">
                          <DollarSign size={14} className="text-green-500" />
                          <span className="font-medium">R$ {parseFloat(caravana.valor || 0).toFixed(2)}</span>
                        </div>
                      </td>
                      <td data-label="Valor Pago">
                        <div className="flex items-center gap-1">
                          <Check size={14} className={valorPago > 0 ? "text-blue-500" : "text-gray-400"} />
                          <span>R$ {valorPago.toFixed(2)}</span>
                        </div>
                      </td>
                      <td data-label="Ações">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleListarParcelas(caravana)}
                            className="btn btn-sm btn-info"
                            title="Parcelas"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(caravana)}
                            className="btn btn-sm btn-secondary"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(caravana)}
                            className="btn btn-sm btn-danger"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Caravana */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">
                ×
              </button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={form.values.nome}
                  onChange={(e) => form.setValue('nome', e.target.value)}
                  onBlur={() => form.setTouched('nome')}
                  className={`form-input ${form.errors.nome && form.touched.nome ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome da caravana"
                />
                {form.errors.nome && form.touched.nome && (
                  <div className="form-error">{form.errors.nome}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Igreja *</label>
                <select
                  value={form.values.igreja_id}
                  onChange={(e) => form.setValue('igreja_id', e.target.value)}
                  onBlur={() => form.setTouched('igreja_id')}
                  className={`form-select ${form.errors.igreja_id && form.touched.igreja_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione uma igreja</option>
                  {igrejas.map((igreja) => (
                    <option key={igreja.id} value={igreja.id}>
                      {igreja.nome}
                    </option>
                  ))}
                </select>
                {form.errors.igreja_id && form.touched.igreja_id && (
                  <div className="form-error">{form.errors.igreja_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Evento *</label>
                <select
                  value={form.values.evento_id}
                  onChange={(e) => form.setValue('evento_id', e.target.value)}
                  onBlur={() => form.setTouched('evento_id')}
                  className={`form-select ${form.errors.evento_id && form.touched.evento_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um evento</option>
                  {eventos.map((evento) => (
                    <option key={evento.id} value={evento.id}>
                      {evento.nome}
                    </option>
                  ))}
                </select>
                {form.errors.evento_id && form.touched.evento_id && (
                  <div className="form-error">{form.errors.evento_id}</div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantidade de Homens *</label>
                  <input
                    type="number"
                    value={form.values.qtd_homens}
                    onChange={(e) => form.setValue('qtd_homens', e.target.value)}
                    onBlur={() => form.setTouched('qtd_homens')}
                    className={`form-input ${form.errors.qtd_homens && form.touched.qtd_homens ? 'border-red-500' : ''}`}
                    placeholder="Quantidade de homens"
                    min="0"
                  />
                  {form.errors.qtd_homens && form.touched.qtd_homens && (
                    <div className="form-error">{form.errors.qtd_homens}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Quantidade de Mulheres *</label>
                  <input
                    type="number"
                    value={form.values.qtd_mulheres}
                    onChange={(e) => form.setValue('qtd_mulheres', e.target.value)}
                    onBlur={() => form.setTouched('qtd_mulheres')}
                    className={`form-input ${form.errors.qtd_mulheres && form.touched.qtd_mulheres ? 'border-red-500' : ''}`}
                    placeholder="Quantidade de mulheres"
                    min="0"
                  />
                  {form.errors.qtd_mulheres && form.touched.qtd_mulheres && (
                    <div className="form-error">{form.errors.qtd_mulheres}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Valor Fechado</label>
                <input
                  type="number"
                  value={form.values.valor}
                  onChange={(e) => form.setValue('valor', e.target.value)}
                  onBlur={() => form.setTouched('valor')}
                  className={`form-input ${form.errors.valor && form.touched.valor ? 'border-red-500' : ''}`}
                  placeholder="Valor total"
                  step="0.01"
                  min="0"
                />
                {form.errors.valor && form.touched.valor && (
                  <div className="form-error">{form.errors.valor}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Observação</label>
                <textarea
                  value={form.values.observacao}
                  onChange={(e) => form.setValue('observacao', e.target.value)}
                  className="form-input"
                  placeholder="Digite observações adicionais"
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Parcelas */}
      {parcelasModal.isOpen && (
        <div className="modal-overlay" onClick={parcelasModal.close}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {parcelasModal.data.mode === 'addParcela' || parcelasModal.data.mode === 'editParcela' 
                  ? parcelasModal.data.title 
                  : parcelasModal.data.title}
              </h2>
              <button onClick={parcelasModal.close} className="modal-close">
                ×
              </button>
            </div>

            {parcelasModal.data.mode === 'addParcela' || parcelasModal.data.mode === 'editParcela' ? (
              <form onSubmit={parcelaForm.handleSubmit(handleSubmitParcela)}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Número da Parcela *</label>
                    <input
                      type="number"
                      value={parcelaForm.values.numero_parcela}
                      onChange={(e) => parcelaForm.setValue('numero_parcela', e.target.value)}
                      onBlur={() => parcelaForm.setTouched('numero_parcela')}
                      className="form-input"
                      placeholder="Ex: 1"
                      min="1"
                    />
                    {parcelaForm.errors.numero_parcela && parcelaForm.touched.numero_parcela && (
                      <div className="form-error">{parcelaForm.errors.numero_parcela}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valor *</label>
                    <input
                      type="number"
                      value={parcelaForm.values.valor}
                      onChange={(e) => parcelaForm.setValue('valor', e.target.value)}
                      onBlur={() => parcelaForm.setTouched('valor')}
                      className="form-input"
                      placeholder="Valor da parcela"
                      step="0.01"
                      min="0"
                    />
                    {parcelaForm.errors.valor && parcelaForm.touched.valor && (
                      <div className="form-error">{parcelaForm.errors.valor}</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Data de Vencimento</label>
                    <input
                      type="date"
                      value={parcelaForm.values.data_vencimento}
                      onChange={(e) => parcelaForm.setValue('data_vencimento', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Pagamento</label>
                    <input
                      type="date"
                      value={parcelaForm.values.data_pagamento}
                      onChange={(e) => parcelaForm.setValue('data_pagamento', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    value={parcelaForm.values.status}
                    onChange={(e) => parcelaForm.setValue('status', e.target.value)}
                    className="form-select"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="pago">Pago</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Observação</label>
                  <textarea
                    value={parcelaForm.values.observacao}
                    onChange={(e) => parcelaForm.setValue('observacao', e.target.value)}
                    className="form-input"
                    placeholder="Observações"
                    rows="2"
                  />
                </div>

                <div className="modal-footer">
                  <button 
                    type="button" 
                    onClick={() => {
                      parcelasModal.open({ title: `Parcelas de ${caravanatSelecionada?.nome}` });
                    }} 
                    className="btn btn-secondary"
                  >
                    Voltar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Salvar
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div className="mb-4">
                  <button onClick={handleAdicionarParcela} className="btn btn-primary">
                    <Plus size={20} />
                    Adicionar Parcela
                  </button>
                </div>

                {parcelasLoading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                  </div>
                ) : parcelas.length === 0 ? (
                  <div className="empty-state">
                    <FileText className="empty-state-icon" />
                    <h3 className="text-lg font-medium mb-2">Nenhuma parcela encontrada</h3>
                    <p className="mb-4">Comece adicionando uma parcela.</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Parcela</th>
                          <th>Valor</th>
                          <th>Vencimento</th>
                          <th>Pagamento</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcelas.map((parcela) => (
                          <tr key={parcela.id}>
                            <td data-label="Parcela">#{parcela.numero_parcela}</td>
                            <td className="font-medium" data-label="Valor">R$ {parseFloat(parcela.valor).toFixed(2)}</td>
                            <td data-label="Vencimento">{parcela.data_vencimento ? new Date(parcela.data_vencimento).toLocaleDateString() : '-'}</td>
                            <td data-label="Pagamento">{parcela.data_pagamento ? new Date(parcela.data_pagamento).toLocaleDateString() : '-'}</td>
                            <td data-label="Status">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                parcela.status === 'pago' ? 'bg-green-100 text-green-800' :
                                parcela.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {parcela.status.toUpperCase()}
                              </span>
                            </td>
                            <td data-label="Ações">
                              <div className="flex gap-2">
                                {parcela.status !== 'pago' && (
                                  <button
                                    onClick={() => handlePagarParcela(parcela)}
                                    className="btn btn-sm btn-success"
                                    title="Registrar Pagamento"
                                  >
                                    <Check size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeletarParcela(parcela)}
                                  className="btn btn-sm btn-danger"
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" onClick={parcelasModal.close} className="btn btn-secondary">
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Caravanas;